import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Calendar, MapPin, Trophy, Clock, Radio, ChevronLeft, 
  ChevronRight, Play, Users, GitBranch, ChevronDown, ExternalLink,
  Share2, Download, Check, Copy, Target, Award, Lock, Video, BarChart3
} from "lucide-react";
import { Button } from "./ui/button";
import { ASNLoader } from "./ASNLoader";

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EventPage = ({ prefetchedEvent = null }) => {
  const { eventSlug } = useParams();
  const [event, setEvent] = useState(prefetchedEvent);
  const [brackets, setBrackets] = useState([]);
  const [loading, setLoading] = useState(!prefetchedEvent);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("schedule");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sponsorScrollRef = useRef(null);

  const fetchBrackets = async (eventId) => {
    try {
      const bracketsRes = await axios.get(`${API}/events/${eventId}/brackets`);
      setBrackets(bracketsRes.data || []);
    } catch (err) {
      console.error("Error fetching brackets:", err);
    }
  };
    
  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/slug/${eventSlug}`);
      setEvent(response.data);
      
      // Fetch brackets if enabled
      if (response.data.brackets_enabled) {
        fetchBrackets(response.data.id);
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      setError("Event not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("userToken");
    setIsLoggedIn(!!token);
    
    // If we have prefetched event, use it and fetch brackets
    if (prefetchedEvent) {
      setEvent(prefetchedEvent);
      if (prefetchedEvent.brackets_enabled) {
        fetchBrackets(prefetchedEvent.id);
      }
      setLoading(false);
      return;
    }
    
    // Otherwise fetch by slug
    if (eventSlug) {
      fetchEvent();
    }
  }, [eventSlug, prefetchedEvent]);

  // Auto-scroll sponsors
  useEffect(() => {
    if (!event?.sponsors?.length || event.sponsors.length <= 3) return;

    const scrollContainer = sponsorScrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scrollSpeed = 1;
    const contentWidth = scrollContainer.scrollWidth / 2;

    const animate = () => {
      scrollPosition += scrollSpeed;
      if (scrollPosition >= contentWidth) {
        scrollPosition = 0;
      }
      scrollContainer.scrollLeft = scrollPosition;
    };

    const interval = setInterval(animate, 30);
    return () => clearInterval(interval);
  }, [event?.sponsors]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        
        <div className="flex items-center justify-center py-32">
          <ASNLoader size="large" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-[#050505]">
        
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h1 className="font-secondary text-2xl font-bold text-white mb-2">Event Not Found</h1>
          <p className="text-zinc-500 mb-6">The event you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link to="/">
            <Button className="bg-white text-black hover:bg-zinc-200 text-xs px-6 py-2 h-auto rounded-none">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasBrackets = event.brackets_enabled && brackets.length > 0;

  // If no brackets, just show schedule (no tabs)
  if (!hasBrackets) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="event-page">
        <EventSEOHead event={event} />
        
        <EventHero event={event} />
        <SponsorBanners event={event} position="top" />
        <SponsorSlideshow event={event} sponsorScrollRef={sponsorScrollRef} />
        <ScheduleSection event={event} formatTime={formatTime} />
        <SponsorBanners event={event} position="between" />
        <VideoPlaylistSection event={event} />
        <EventDetails event={event} formatDate={formatDate} />
        <SponsorBanners event={event} position="bottom" />
        <SponsorsFooter event={event} />
      </div>
    );
  }

  // With brackets, show tabbed interface
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="event-page">
      <EventSEOHead event={event} />
      
      <EventHero event={event} />
      
      {/* Sponsor Banners - top position */}
      <SponsorBanners event={event} position="top" />
      
      {/* Horizontal Teams Bar with Bracket Dropdown */}
      <TeamsBar brackets={brackets} eventColor={event.color} />
      
      <SponsorSlideshow event={event} sponsorScrollRef={sponsorScrollRef} />

      {/* Tabs */}
      <div className="border-b border-white/10 bg-black/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab("schedule")}
              className={`px-6 py-4 text-sm font-bold uppercase transition-colors whitespace-nowrap ${
                activeTab === "schedule" 
                  ? "text-white border-b-2" 
                  : "text-zinc-500 hover:text-white"
              }`}
              style={{ borderColor: activeTab === "schedule" ? event.color : "transparent" }}
            >
              <Radio className="w-4 h-4 inline mr-2" />
              Schedule
            </button>
            <button
              onClick={() => setActiveTab("bracket")}
              className={`px-6 py-4 text-sm font-bold uppercase transition-colors whitespace-nowrap ${
                activeTab === "bracket" 
                  ? "text-white border-b-2" 
                  : "text-zinc-500 hover:text-white"
              }`}
              style={{ borderColor: activeTab === "bracket" ? event.color : "transparent" }}
            >
              <GitBranch className="w-4 h-4 inline mr-2" />
              Bracket
            </button>
            <button
              onClick={() => setActiveTab("teams")}
              className={`px-6 py-4 text-sm font-bold uppercase transition-colors whitespace-nowrap ${
                activeTab === "teams" 
                  ? "text-white border-b-2" 
                  : "text-zinc-500 hover:text-white"
              }`}
              style={{ borderColor: activeTab === "teams" ? event.color : "transparent" }}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Teams
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content with Stat Leaders Sidebar */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {activeTab === "schedule" && <ScheduleSection event={event} formatTime={formatTime} />}
            {activeTab === "bracket" && <ESPNBracketSection brackets={brackets} eventColor={event.color} />}
            {activeTab === "teams" && <TeamsSection brackets={brackets} eventColor={event.color} />}
          </div>

          {/* Stat Leaders Sidebar */}
          {event.stat_leaders_enabled && brackets.length > 0 && (
            <div className="hidden lg:block w-[280px] flex-shrink-0">
              <StatLeadersSidebar eventId={event.id} brackets={brackets} eventColor={event.color} />
            </div>
          )}
        </div>
      </div>

      {/* Video Playlist */}
      <VideoPlaylistSection event={event} />
      
      {/* Additional banners between sections */}
      <SponsorBanners event={event} position="between" />

      <EventDetails event={event} formatDate={formatDate} />
      
      {/* Bottom banners */}
      <SponsorBanners event={event} position="bottom" />
      
      <SponsorsFooter event={event} />
    </div>
  );
};

// Horizontal Teams Bar with Bracket Dropdown - Logo only design
const TeamsBar = ({ brackets, eventColor }) => {
  const [selectedBracket, setSelectedBracket] = useState(brackets[0]?.id || null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const scrollRef = useRef(null);
  
  const currentBracket = brackets.find(b => b.id === selectedBracket);
  const teams = currentBracket?.teams || [];

  if (!teams.length) return null;

  // Scroll functions for mobile swipe hint
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-3 bg-black/30 border-y border-white/5">
      <div className="w-full px-2 md:px-4">
        <div className="flex items-center gap-2">
          {/* Bracket Dropdown (if multiple brackets) */}
          {brackets.length > 1 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-white/10 text-white text-sm hover:bg-zinc-700"
                data-testid="bracket-selector"
              >
                <Trophy className="w-4 h-4" style={{ color: eventColor }} />
                <span className="hidden sm:inline">{currentBracket?.name || "Select"}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-zinc-900 border border-white/10 z-40 min-w-[180px]">
                  {brackets.map(bracket => (
                    <button
                      key={bracket.id}
                      onClick={() => { setSelectedBracket(bracket.id); setDropdownOpen(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 ${
                        selectedBracket === bracket.id ? 'bg-white/5 text-white' : 'text-zinc-400'
                      }`}
                    >
                      {bracket.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Scroll left button (visible on mobile) */}
          <button 
            onClick={scrollLeft}
            className="md:hidden flex-shrink-0 p-1 text-zinc-500 hover:text-white"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Horizontal Team Logos - Full width, swipeable */}
          <div 
            ref={scrollRef}
            className="flex items-center gap-3 overflow-x-auto scrollbar-hide flex-1 scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {teams.map((team, idx) => (
              <TeamLogoOnly key={team.id} team={team} eventColor={eventColor} />
            ))}
          </div>

          {/* Scroll right button (visible on mobile) */}
          <button 
            onClick={scrollRight}
            className="md:hidden flex-shrink-0 p-1 text-zinc-500 hover:text-white"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Team Logo Only Component - Proportional, minimal
const TeamLogoOnly = ({ team, eventColor }) => {
  const logoSize = "w-10 h-10 md:w-12 md:h-12";
  
  const content = (
    <div 
      className={`${logoSize} flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden border-2 hover:scale-110 transition-transform cursor-pointer`}
      style={{ 
        borderColor: team.color || eventColor,
        backgroundColor: (team.color || eventColor) + '15'
      }}
      title={team.name}
    >
      {team.logo_url ? (
        <img 
          src={team.logo_url} 
          alt={team.name} 
          className="w-7 h-7 md:w-8 md:h-8 object-contain"
        />
      ) : (
        <span 
          className="text-xs md:text-sm font-bold"
          style={{ color: team.color || eventColor }}
        >
          {team.name.charAt(0)}
        </span>
      )}
    </div>
  );

  if (team.website_url) {
    return (
      <a href={team.website_url} target="_blank" rel="noopener noreferrer" title={team.name}>
        {content}
      </a>
    );
  }

  return content;
};

// Event Hero Component
const EventHero = ({ event }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <section 
      className="relative pt-8 pb-12 md:pt-10 md:pb-16"
      style={{ 
        background: `linear-gradient(135deg, ${event.color}30 0%, #050505 50%, ${event.color}10 100%)`
      }}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Event Logo */}
          <div 
            className="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center flex-shrink-0 rounded-lg"
            style={{ backgroundColor: event.color + '20' }}
          >
            {event.logo_url ? (
              <img 
                src={event.logo_url} 
                alt={event.name} 
                className="w-24 h-24 md:w-32 md:h-32 object-contain"
              />
            ) : (
              <Trophy className="w-16 h-16 md:w-20 md:h-20" style={{ color: event.color }} />
            )}
          </div>

          {/* Event Info */}
          <div className="text-center md:text-left flex-1">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <span 
                className="px-3 py-1 text-xs font-bold uppercase"
                style={{ backgroundColor: event.color, color: '#000' }}
              >
                {event.sport}
              </span>
            </div>
            <h1 className="font-secondary text-3xl md:text-5xl font-black uppercase text-white mb-4">
              {event.name}
            </h1>
            
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-zinc-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(event.start_date)}
                {event.end_date && event.end_date !== event.start_date && (
                  <> - {formatDate(event.end_date)}</>
                )}
              </span>
              {event.location && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {event.venue && `${event.venue}, `}{event.location}
                </span>
              )}
            </div>

            {event.description && (
              <p className="mt-4 text-zinc-400 max-w-2xl">{event.description}</p>
            )}
          </div>

          {/* Flagship Sponsor */}
          {event.flagship_sponsor_enabled && event.flagship_sponsor_logo_url && (
            <div className="flex flex-col items-center gap-2 p-4 bg-black/30 border border-white/10 rounded-lg">
              <span className="text-[10px] text-amber-500 font-bold uppercase tracking-wider">Presented By</span>
              {event.flagship_sponsor_url ? (
                <a href={event.flagship_sponsor_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <img 
                    src={event.flagship_sponsor_logo_url} 
                    alt={event.flagship_sponsor_name || "Flagship Sponsor"} 
                    className="h-12 md:h-16 max-w-[120px] md:max-w-[150px] object-contain"
                  />
                </a>
              ) : (
                <img 
                  src={event.flagship_sponsor_logo_url} 
                  alt={event.flagship_sponsor_name || "Flagship Sponsor"} 
                  className="h-12 md:h-16 max-w-[120px] md:max-w-[150px] object-contain"
                />
              )}
              {event.flagship_sponsor_name && (
                <span className="text-xs text-zinc-500">{event.flagship_sponsor_name}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Sponsor Slideshow Component - Shows sponsor LOGOS (not banners) in scrolling carousel
const SponsorSlideshow = ({ event, sponsorScrollRef }) => {
  if (!event.sponsors?.length) return null;

  // Get the display URL for a sponsor - prefer logo for slideshow
  const getLogoUrl = (sponsor) => sponsor.logo_file || sponsor.logo_url;
  
  // Get unique sponsors by id or name to avoid duplicates
  const uniqueSponsors = [];
  const seenIds = new Set();
  
  for (const sponsor of event.sponsors) {
    const key = sponsor.id || sponsor.name;
    if (!seenIds.has(key) && getLogoUrl(sponsor)) {
      seenIds.add(key);
      uniqueSponsors.push(sponsor);
    }
  }
  
  if (uniqueSponsors.length === 0) return null;

  return (
    <section className="py-6 bg-black/50 border-y border-white/10 overflow-hidden" data-testid="sponsor-slideshow">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h3 className="font-secondary text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4 text-center">
          Event Sponsors
        </h3>
        
        <div 
          ref={sponsorScrollRef}
          className="flex items-center justify-center gap-8 overflow-hidden"
          style={{ scrollBehavior: 'auto' }}
        >
          {/* Duplicate sponsors for infinite scroll effect (only if enough sponsors) */}
          {(uniqueSponsors.length > 3 ? [...uniqueSponsors, ...uniqueSponsors] : uniqueSponsors).map((sponsor, idx) => (
            <div 
              key={`${sponsor.id || sponsor.name}-${idx}`} 
              className="flex-shrink-0 flex flex-col items-center gap-2"
            >
              {sponsor.url ? (
                <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <img 
                    src={getLogoUrl(sponsor)} 
                    alt={sponsor.name} 
                    className="h-12 md:h-16 max-w-[150px] object-contain"
                  />
                </a>
              ) : (
                <img 
                  src={getLogoUrl(sponsor)} 
                  alt={sponsor.name} 
                  className="h-12 md:h-16 max-w-[150px] object-contain"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Schedule Section Component
const ScheduleSection = ({ event, formatTime }) => {
  const upcomingBroadcasts = event.broadcasts?.filter(b => {
    const now = new Date();
    const broadcastDate = new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`);
    return broadcastDate >= now || b.is_live;
  }) || [];

  const pastBroadcasts = event.broadcasts?.filter(b => {
    const now = new Date();
    const broadcastDate = new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`);
    return broadcastDate < now && !b.is_live;
  }) || [];

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h2 className="font-secondary text-2xl font-bold uppercase text-white mb-8 flex items-center gap-3">
          <Radio className="w-6 h-6" style={{ color: event.color }} />
          Event Schedule
        </h2>

        {event.broadcasts?.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Schedule coming soon</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming/Live Broadcasts */}
            {upcomingBroadcasts.length > 0 && (
              <div>
                <h3 className="text-sm font-bold uppercase text-zinc-400 mb-4">Upcoming Games</h3>
                <div className="grid gap-4">
                  {upcomingBroadcasts.map(broadcast => (
                    <BroadcastCard key={broadcast.id} broadcast={broadcast} eventColor={event.color} formatTime={formatTime} />
                  ))}
                </div>
              </div>
            )}

            {/* Past Broadcasts */}
            {pastBroadcasts.length > 0 && (
              <div>
                <h3 className="text-sm font-bold uppercase text-zinc-400 mb-4">Completed Games</h3>
                <div className="grid gap-4 opacity-70">
                  {pastBroadcasts.map(broadcast => (
                    <BroadcastCard key={broadcast.id} broadcast={broadcast} eventColor={event.color} formatTime={formatTime} isPast />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// ESPN-Style Bracket Section Component
const ESPNBracketSection = ({ brackets, eventColor }) => {
  const [selectedBracket, setSelectedBracket] = useState(brackets[0]?.id || null);
  
  const currentBracket = brackets.find(b => b.id === selectedBracket);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const bracketRef = useRef(null);

  const getTeamById = (teamId) => {
    if (!teamId || !currentBracket) return null;
    return currentBracket.teams?.find(t => t.id === teamId);
  };

  const getRoundGames = (roundNumber, isLosers = false) => {
    return currentBracket?.games?.filter(g => 
      g.round_number === roundNumber && g.is_losers_bracket === isLosers
    ).sort((a, b) => a.position - b.position) || [];
  };

  const getRoundTitle = (roundNumber) => {
    const round = currentBracket?.rounds?.find(r => r.round_number === roundNumber);
    return round?.title || `Round ${roundNumber}`;
  };

  const getRoundLocation = (roundNumber) => {
    const round = currentBracket?.rounds?.find(r => r.round_number === roundNumber);
    return round?.location || "";
  };

  const winnersRounds = [...new Set(
    currentBracket?.games?.filter(g => !g.is_losers_bracket).map(g => g.round_number)
  )].sort((a, b) => a - b);

  const losersRounds = [...new Set(
    currentBracket?.games?.filter(g => g.is_losers_bracket).map(g => g.round_number)
  )].sort((a, b) => a - b);

  // Share bracket link
  const handleShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Export bracket as PDF
  const handleExportPDF = async () => {
    if (!bracketRef.current) return;
    
    // Set PDF mode to hide watch buttons
    setIsExportingPdf(true);
    
    // Wait for re-render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Dynamic import for PDF generation
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const canvas = await html2canvas(bracketRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${currentBracket?.name || 'bracket'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF export requires additional libraries. Please use the share link instead.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (!currentBracket) {
    return (
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <GitBranch className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No brackets available</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header with Bracket Selector and Share/Export */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          {/* Bracket Selector */}
          {brackets.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              {brackets.map(bracket => (
                <button
                  key={bracket.id}
                  onClick={() => setSelectedBracket(bracket.id)}
                  className={`px-4 py-2 text-sm font-bold uppercase transition-colors ${
                    selectedBracket === bracket.id 
                      ? 'text-black' 
                      : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                  style={{ backgroundColor: selectedBracket === bracket.id ? eventColor : undefined }}
                >
                  {bracket.name}
                </button>
              ))}
            </div>
          ) : <div></div>}
          
          {/* Share & Export Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleShareLink}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition-colors rounded"
            >
              {linkCopied ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Link Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  <span>Share</span>
                </>
              )}
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition-colors rounded"
            >
              <Download className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
          </div>
        </div>

        <h2 className="font-secondary text-2xl font-bold uppercase text-white mb-2 flex items-center gap-3">
          <GitBranch className="w-6 h-6" style={{ color: eventColor }} />
          {currentBracket.name}
        </h2>
        <p className="text-sm text-zinc-500 mb-8">
          {currentBracket.bracket_type === "double" ? "Double Elimination" : "Single Elimination"} • {currentBracket.teams?.length || 0} Teams
        </p>

        {/* ESPN-Style Bracket Visualization */}
        <div className="overflow-x-auto pb-4" ref={bracketRef}>
          <div className="min-w-[900px] p-4 bg-zinc-950 rounded-lg">
            {/* Winners Bracket */}
            <div className="mb-12">
              {currentBracket.bracket_type === "double" && (
                <h3 className="text-sm font-bold uppercase text-zinc-400 mb-6">Winners Bracket</h3>
              )}
              <div className="flex items-stretch gap-6">
                {winnersRounds.map((roundNum, roundIdx) => {
                  const games = getRoundGames(roundNum, false);
                  const isLastRound = roundIdx === winnersRounds.length - 1;
                  
                  return (
                    <div key={roundNum} className="flex flex-col" style={{ minWidth: isLastRound ? '200px' : '220px' }}>
                      {/* Round Header */}
                      <div className="text-center mb-4 px-2">
                        <div className="text-xs font-bold uppercase text-zinc-400">{getRoundTitle(roundNum)}</div>
                        {getRoundLocation(roundNum) && (
                          <div className="text-[10px] text-zinc-600 flex items-center justify-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {getRoundLocation(roundNum)}
                          </div>
                        )}
                      </div>
                      
                      {/* Games with ESPN-style spacing */}
                      <div className="flex flex-col justify-around flex-1" style={{ gap: `${Math.pow(2, roundIdx) * 20}px` }}>
                        {games.map((game, gameIdx) => (
                          <ESPNGameCard 
                            key={game.id} 
                            game={game} 
                            getTeamById={getTeamById} 
                            eventColor={eventColor}
                            forPdf={isExportingPdf}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Losers Bracket (Double Elimination) */}
            {currentBracket.bracket_type === "double" && losersRounds.length > 0 && (
              <div className="pt-8 mt-8 border-t border-white/10">
                <h3 className="text-sm font-bold uppercase text-red-400 mb-6">Losers Bracket</h3>
                <div className="flex items-stretch gap-6">
                  {losersRounds.map((roundNum, roundIdx) => {
                    const games = getRoundGames(roundNum, true);
                    const isLastRound = roundIdx === losersRounds.length - 1;
                    
                    return (
                      <div key={roundNum} className="flex flex-col" style={{ minWidth: isLastRound ? '200px' : '220px' }}>
                        <div className="text-center mb-4 px-2">
                          <div className="text-xs font-bold uppercase text-zinc-400">{getRoundTitle(roundNum)}</div>
                        </div>
                        
                        <div className="flex flex-col justify-around flex-1" style={{ gap: `${Math.pow(2, roundIdx % 3) * 16}px` }}>
                          {games.map((game, gameIdx) => (
                            <ESPNGameCard 
                              key={game.id} 
                              game={game} 
                              getTeamById={getTeamById} 
                              eventColor={eventColor}
                              isLosers
                              forPdf={isExportingPdf}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ESPN-Style Game Card Team Row Component (moved outside to avoid render issues)
const ESPNTeamRow = ({ team, score, isWinner, forPdf = false }) => {
  const content = (
    <div className={`flex items-center gap-2 px-3 py-2.5 ${isWinner ? 'bg-green-500/10' : 'bg-zinc-900/80'}`}>
      {team ? (
        <>
          {/* Seed Badge */}
          {team.seed && (
            <span className="text-[10px] text-yellow-500 font-bold w-4 text-center flex-shrink-0">{team.seed}</span>
          )}
          {team.logo_url ? (
            <img src={team.logo_url} alt={team.name} className="w-5 h-5 object-contain flex-shrink-0" />
          ) : (
            <div 
              className="w-5 h-5 rounded-sm flex items-center justify-center text-[9px] font-bold flex-shrink-0"
              style={{ backgroundColor: team.color + '30', color: team.color }}
            >
              {team.name.charAt(0)}
            </div>
          )}
          <span 
            className={`text-xs flex-1 ${isWinner ? 'text-white font-semibold' : 'text-zinc-300'}`}
            style={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: forPdf ? 'normal' : 'nowrap',
              lineHeight: '1.2'
            }}
            title={team.name}
          >
            {team.name}
          </span>
          {score !== null && (
            <span className={`text-sm font-bold min-w-[24px] text-right flex-shrink-0 ${isWinner ? 'text-green-400' : 'text-zinc-400'}`}>
              {score}
            </span>
          )}
        </>
      ) : (
        <span className="text-xs text-zinc-600 italic">TBD</span>
      )}
    </div>
  );

  // Don't wrap in links for PDF export
  if (forPdf) {
    return content;
  }

  if (team?.website_url) {
    return (
      <a href={team.website_url} target="_blank" rel="noopener noreferrer" className="block hover:bg-white/5">
        {content}
      </a>
    );
  }

  return content;
};

// ESPN-Style Game Card Component
const ESPNGameCard = ({ game, getTeamById, eventColor, isLosers = false, forPdf = false }) => {
  const team1 = getTeamById(game.team1_id);
  const team2 = getTeamById(game.team2_id);

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="relative">
      {/* Game Card - Clean design without border lines */}
      <div className={`${forPdf ? 'w-[220px]' : 'w-[200px]'} bg-zinc-900 rounded overflow-hidden shadow-lg`}>
        <ESPNTeamRow team={team1} score={game.team1_score} isWinner={game.winner_id === game.team1_id} forPdf={forPdf} />
        <div className="h-px bg-zinc-800"></div>
        <ESPNTeamRow team={team2} score={game.team2_score} isWinner={game.winner_id === game.team2_id} forPdf={forPdf} />
        
        {/* Game Info Footer - Hide watch button in PDF */}
        {(game.game_date || game.game_time || (!forPdf && game.broadcast_id)) && (
          <div className="px-3 py-1.5 bg-zinc-800/50 flex items-center justify-between">
            <div className="text-[10px] text-zinc-500">
              {game.game_date && <span>{game.game_date}</span>}
              {game.game_time && <span className="ml-1">{formatTime(game.game_time)}</span>}
            </div>
            {!forPdf && game.broadcast_id && (
              <Link to={`/game/${game.broadcast_id}`} className="text-[10px] px-2 py-0.5 bg-white/10 hover:bg-white/20 text-white rounded">
                Watch
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Teams Section Component
const TeamsSection = ({ brackets, eventColor }) => {
  const [selectedBracket, setSelectedBracket] = useState("all");
  
  // Collect teams based on selection
  let displayTeams = [];
  
  if (selectedBracket === "all") {
    const seenTeamIds = new Set();
    brackets.forEach(bracket => {
      bracket.teams?.forEach(team => {
        if (!seenTeamIds.has(team.id)) {
          seenTeamIds.add(team.id);
          displayTeams.push({ ...team, bracketName: bracket.name });
        }
      });
    });
  } else {
    const bracket = brackets.find(b => b.id === selectedBracket);
    displayTeams = (bracket?.teams || []).map(t => ({ ...t, bracketName: bracket.name }));
  }

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-secondary text-2xl font-bold uppercase text-white flex items-center gap-3">
            <Users className="w-6 h-6" style={{ color: eventColor }} />
            Participating Teams
          </h2>
          
          {/* Bracket Filter */}
          {brackets.length > 1 && (
            <select
              value={selectedBracket}
              onChange={(e) => setSelectedBracket(e.target.value)}
              className="bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2"
            >
              <option value="all">All Brackets</option>
              {brackets.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
        </div>

        {displayTeams.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Teams coming soon</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {displayTeams.map(team => (
              <TeamCard key={team.id} team={team} brackets={brackets} eventColor={eventColor} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Team Card Component
const TeamCard = ({ team, brackets, eventColor }) => {
  const content = (
    <div 
      className="p-4 bg-zinc-900/50 border border-white/10 hover:border-white/20 transition-colors"
      style={{ borderLeftColor: team.color, borderLeftWidth: 4 }}
    >
      <div className="flex items-center gap-4">
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.name} className="w-12 h-12 object-contain" />
        ) : (
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
            style={{ backgroundColor: team.color + '30', color: team.color }}
          >
            {team.name.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-secondary text-sm font-bold text-white flex items-center gap-2">
            {team.name}
            {team.website_url && <ExternalLink className="w-3 h-3 text-zinc-500" />}
          </h3>
          {brackets.length > 1 && (
            <p className="text-[10px] text-zinc-500">{team.bracketName}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (team.website_url) {
    return (
      <a href={team.website_url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return content;
};

// Broadcast Card Component
const BroadcastCard = ({ broadcast, eventColor, formatTime, isPast = false }) => {
  const hasTeams = broadcast.home_team?.name && broadcast.away_team?.name;
  const hasWatch = broadcast.is_live || broadcast.youtube_url || broadcast.recording_url || broadcast.youtube_vod_url;
  const hasStats = broadcast.stats_link;
  const gameUrl = `/game/${broadcast.id}`;
  
  return (
    <Link 
      to={gameUrl}
      className="flex items-center gap-4 p-4 bg-black/50 border border-white/10 hover:border-white/20 transition-colors cursor-pointer"
      data-testid={`broadcast-card-${broadcast.id}`}
    >
      {/* Date + Team Logos */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Date Column */}
        <div className="w-14 text-center">
          <div className="text-xs text-zinc-500 uppercase">
            {new Date(broadcast.scheduled_date + "T00:00:00").toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          <div className="text-2xl font-bold text-white">
            {new Date(broadcast.scheduled_date + "T00:00:00").getDate()}
          </div>
          <div className="text-xs text-zinc-500">
            {new Date(broadcast.scheduled_date + "T00:00:00").toLocaleDateString('en-US', { month: 'short' })}
          </div>
        </div>

        {/* Team Logos with VS badge */}
        {hasTeams && (
          <div className="flex items-center gap-1">
            {/* Away Team Logo */}
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden border-2"
              style={{ borderColor: broadcast.away_team.color || '#333', backgroundColor: broadcast.away_team.color + '20' || '#33333320' }}
            >
              {broadcast.away_team.logo_url ? (
                <img src={broadcast.away_team.logo_url} alt={broadcast.away_team.name} className="w-7 h-7 object-contain" />
              ) : (
                <span className="text-xs font-bold text-white">{(broadcast.away_team.abbreviation || broadcast.away_team.name).slice(0, 2)}</span>
              )}
            </div>
            
            {/* VS Badge */}
            <span className="px-1.5 py-0.5 text-[10px] font-bold text-zinc-500 bg-zinc-800 rounded">vs</span>
            
            {/* Home Team Logo */}
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden border-2"
              style={{ borderColor: broadcast.home_team.color || '#333', backgroundColor: broadcast.home_team.color + '20' || '#33333320' }}
            >
              {broadcast.home_team.logo_url ? (
                <img src={broadcast.home_team.logo_url} alt={broadcast.home_team.name} className="w-7 h-7 object-contain" />
              ) : (
                <span className="text-xs font-bold text-white">{(broadcast.home_team.abbreviation || broadcast.home_team.name).slice(0, 2)}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Game Info - Matchup on top, title below */}
      <div className="flex-1 min-w-0">
        {/* Live Badge + Matchup (Bold, Primary) */}
        <div className="flex items-center gap-2 mb-1">
          {broadcast.is_live && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] font-black uppercase">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
              Live
            </span>
          )}
          {hasTeams ? (
            <h4 className="font-secondary text-base font-bold text-white truncate">
              {broadcast.away_team.abbreviation || broadcast.away_team.name} @ {broadcast.home_team.abbreviation || broadcast.home_team.name}
            </h4>
          ) : (
            <h4 className="font-secondary text-base font-bold text-white truncate">{broadcast.title}</h4>
          )}
        </div>
        
        {/* Broadcast Title (Smaller, Lighter) - Only show if has teams */}
        {hasTeams && broadcast.title && (
          <p className="text-sm text-zinc-400 truncate">{broadcast.title}</p>
        )}

        {/* Sport badge only */}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-zinc-500 uppercase tracking-wider">{broadcast.sport}</span>
        </div>
      </div>

      {/* Actions - Watch & Stats buttons OR Time */}
      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {broadcast.is_live ? (
          <>
            <Link to={`/game/${broadcast.id}`} onClick={(e) => e.stopPropagation()}>
              <Button 
                className="text-xs px-4 py-2 h-auto rounded-none flex items-center gap-1"
                style={{ backgroundColor: eventColor, color: '#000' }}
              >
                <Play className="w-4 h-4 fill-current" />
                Watch
              </Button>
            </Link>
            {hasStats && (
              <a href={broadcast.stats_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <Button className="text-xs px-3 py-2 h-auto rounded-none bg-green-600 hover:bg-green-700 text-white flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  Stats
                </Button>
              </a>
            )}
          </>
        ) : isPast ? (
          <>
            {hasWatch && (
              <Link to={`/game/${broadcast.id}`} onClick={(e) => e.stopPropagation()}>
                <Button className="text-xs px-3 py-2 h-auto rounded-none bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1">
                  <Play className="w-4 h-4 fill-current" />
                  Watch
                </Button>
              </Link>
            )}
            {hasStats && (
              <a href={broadcast.stats_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <Button className="text-xs px-3 py-2 h-auto rounded-none bg-green-600 hover:bg-green-700 text-white flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  Stats
                </Button>
              </a>
            )}
          </>
        ) : (
          <div className="text-xs text-right">
            <div className="text-white font-bold">{formatTime(broadcast.scheduled_time)}</div>
          </div>
        )}
      </div>
    </Link>
  );
};

// Event Details Component
const EventDetails = ({ event, formatDate }) => {
  return (
    <section className="py-12 md:py-16 bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h2 className="font-secondary text-2xl font-bold uppercase text-white mb-8">
          Event Details
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-black/50 border border-white/10 p-6">
            <Calendar className="w-8 h-8 mb-4" style={{ color: event.color }} />
            <h3 className="font-secondary text-lg font-bold text-white mb-2">Dates</h3>
            <p className="text-zinc-400">
              {formatDate(event.start_date)}
              {event.end_date && event.end_date !== event.start_date && (
                <><br/>to {formatDate(event.end_date)}</>
              )}
            </p>
          </div>

          <div className="bg-black/50 border border-white/10 p-6">
            <MapPin className="w-8 h-8 mb-4" style={{ color: event.color }} />
            <h3 className="font-secondary text-lg font-bold text-white mb-2">Location</h3>
            <p className="text-zinc-400">
              {event.venue && <>{event.venue}<br/></>}
              {event.location || "TBA"}
            </p>
          </div>

          <div className="bg-black/50 border border-white/10 p-6">
            <Trophy className="w-8 h-8 mb-4" style={{ color: event.color }} />
            <h3 className="font-secondary text-lg font-bold text-white mb-2">Sport</h3>
            <p className="text-zinc-400">{event.sport}</p>
            <p className="text-sm text-zinc-500 mt-1">
              {event.broadcasts?.length || 0} games scheduled
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sponsor Banners Component - Shows sponsor BANNERS in specific positions
const SponsorBanners = ({ event, position = "top" }) => {
  // Get sponsors that have banner images
  const sponsorsWithBanners = (event.sponsors || []).filter(
    sponsor => sponsor.banner_file || sponsor.banner_url
  );
  
  if (sponsorsWithBanners.length === 0) return null;

  const getBannerUrl = (sponsor) => sponsor.banner_file || sponsor.banner_url;
  
  // Split banners into positions - first goes to top, rest alternate between positions
  let bannersToShow = [];
  if (position === "top") {
    // Show first banner at top
    bannersToShow = sponsorsWithBanners.slice(0, 1);
  } else if (position === "between") {
    // Show middle banners between sections
    bannersToShow = sponsorsWithBanners.slice(1, 3);
  } else if (position === "bottom") {
    // Show remaining banners at bottom
    bannersToShow = sponsorsWithBanners.slice(3);
  }
  
  if (bannersToShow.length === 0) return null;

  return (
    <section className={`py-6 ${position === "top" ? "bg-zinc-900/50" : "bg-black/30"}`} data-testid={`sponsor-banners-${position}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col items-center gap-6">
          {bannersToShow.map((sponsor, idx) => (
            <div key={`${sponsor.id || sponsor.name}-${idx}`} className="w-full flex justify-center">
              {sponsor.url ? (
                <a 
                  href={sponsor.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block hover:opacity-90 transition-opacity"
                >
                  <img 
                    src={getBannerUrl(sponsor)} 
                    alt={sponsor.name} 
                    className="max-w-full max-h-[200px] object-contain"
                  />
                </a>
              ) : (
                <img 
                  src={getBannerUrl(sponsor)} 
                  alt={sponsor.name} 
                  className="max-w-full max-h-[200px] object-contain"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sponsors Footer Component - Shows unique sponsors (no duplicates from slideshow)
const SponsorsFooter = ({ event }) => {
  if (!event.sponsors?.length) return null;

  const getLogoUrl = (sponsor) => sponsor.logo_file || sponsor.logo_url;
  
  // Get unique sponsors by id or name to avoid duplicates
  const uniqueSponsors = [];
  const seenIds = new Set();
  
  for (const sponsor of event.sponsors) {
    const key = sponsor.id || sponsor.name;
    if (!seenIds.has(key)) {
      seenIds.add(key);
      uniqueSponsors.push(sponsor);
    }
  }

  return (
    <section className="py-12 border-t border-white/10" data-testid="sponsors-footer">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-zinc-500 mb-6 text-center">
          Thank You to Our Sponsors
        </h3>
        <div className="flex flex-wrap items-center justify-center gap-8">
          {uniqueSponsors.map((sponsor) => (
            <div key={sponsor.id || sponsor.name} className="flex flex-col items-center">
              {getLogoUrl(sponsor) ? (
                sponsor.url ? (
                  <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <img 
                      src={getLogoUrl(sponsor)} 
                      alt={sponsor.name} 
                      className="h-12 max-w-[120px] object-contain grayscale hover:grayscale-0 transition-all"
                    />
                  </a>
                ) : (
                  <img 
                    src={getLogoUrl(sponsor)} 
                    alt={sponsor.name} 
                    className="h-12 max-w-[120px] object-contain grayscale hover:grayscale-0 transition-all"
                  />
                )
              ) : (
                <span className="text-zinc-500">{sponsor.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SEO Head Component
const EventSEOHead = ({ event }) => {
  const seo = event.seo || {};
  const title = seo.meta_title || event.name;
  const description = seo.meta_description || event.description || `${event.name} - ${event.sport} event`;
  const image = seo.og_image_url || event.logo_url;
  
  return (
    <Helmet>
      <title>ASN | {title}</title>
      <meta name="description" content={description} />
      {seo.keywords?.length > 0 && (
        <meta name="keywords" content={seo.keywords.join(", ")} />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
    </Helmet>
  );
};

// Video Playlist Section Component
const VideoPlaylistSection = ({ event }) => {
  if (!event.video_playlist_enabled || !event.video_playlist?.length) return null;

  const extractVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  return (
    <section className="py-12 md:py-16" data-testid="video-playlist-section">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h2 className="font-secondary text-2xl font-bold uppercase text-white mb-8 flex items-center gap-3">
          <Video className="w-6 h-6" style={{ color: event.color }} />
          {event.video_playlist_title || "Event Videos"}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {event.video_playlist.map((video) => {
            const videoId = extractVideoId(video.youtube_url);
            return (
              <div key={video.id} className="group bg-zinc-900/50 border border-white/10 hover:border-white/20 transition-colors">
                <div className="relative aspect-video">
                  {videoId ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : video.thumbnail_url ? (
                    <a href={video.youtube_url} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={video.thumbnail_url} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 group-hover:bg-black/30 transition-colors">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                    </a>
                  ) : (
                    <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-full bg-zinc-800">
                      <Play className="w-12 h-12 text-zinc-600" />
                    </a>
                  )}
                </div>
                {video.title && (
                  <div className="p-3">
                    <h3 className="text-sm text-white font-medium truncate">{video.title}</h3>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Stat Leaders Sidebar Component
const StatLeadersSidebar = ({ eventId, brackets, eventColor }) => {
  const [selectedBracket, setSelectedBracket] = useState(brackets[0]?.id || null);
  const [statLeaders, setStatLeaders] = useState({ points: [], rebounds: [], assists: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatLeaders();
  }, [eventId, selectedBracket]);

  const fetchStatLeaders = async () => {
    setLoading(true);
    try {
      const bracketParam = selectedBracket ? `?bracket_id=${selectedBracket}` : "";
      const res = await axios.get(`${API}/events/${eventId}/stat-leaders${bracketParam}`);
      if (res.data.enabled) {
        setStatLeaders(res.data.stat_leaders || { points: [], rebounds: [], assists: [] });
      }
    } catch (err) {
      console.error("Error fetching stat leaders:", err);
    } finally {
      setLoading(false);
    }
  };

  const hasAnyStats = statLeaders.points?.length > 0 || statLeaders.rebounds?.length > 0 || statLeaders.assists?.length > 0;

  return (
    <div className="sticky top-20 bg-zinc-900/50 border border-white/10" data-testid="stat-leaders-sidebar">
      {/* Header */}
      <div 
        className="p-4 border-b border-white/10"
        style={{ background: `linear-gradient(135deg, ${eventColor}20 0%, transparent 100%)` }}
      >
        <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5" style={{ color: eventColor }} />
          Stat Leaders
        </h3>
      </div>

      {/* Bracket Selector */}
      {brackets.length > 1 && (
        <div className="p-3 border-b border-white/10">
          <select
            value={selectedBracket || ""}
            onChange={(e) => setSelectedBracket(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-2 py-1.5"
          >
            {brackets.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stats Content */}
      <div className="p-4 space-y-6">
        {loading ? (
          <div className="py-8 text-center">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div>
          </div>
        ) : !hasAnyStats ? (
          <div className="py-8 text-center">
            <BarChart3 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No stats available yet</p>
            <p className="text-zinc-600 text-xs mt-1">Stats will appear once games have been played</p>
          </div>
        ) : (
          <>
            {/* Points Leaders */}
            {statLeaders.points?.length > 0 && (
              <StatCategory 
                title="Points" 
                leaders={statLeaders.points} 
                statKey="points"
                color="#F59E0B"
              />
            )}

            {/* Rebounds Leaders */}
            {statLeaders.rebounds?.length > 0 && (
              <StatCategory 
                title="Rebounds" 
                leaders={statLeaders.rebounds} 
                statKey="rebounds"
                color="#10B981"
              />
            )}

            {/* Assists Leaders */}
            {statLeaders.assists?.length > 0 && (
              <StatCategory 
                title="Assists" 
                leaders={statLeaders.assists} 
                statKey="assists"
                color="#3B82F6"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Stat Category Component
const StatCategory = ({ title, leaders, statKey, color }) => {
  return (
    <div>
      <h4 className="text-xs font-bold uppercase text-zinc-400 mb-3" style={{ color }}>
        {title}
      </h4>
      <div className="space-y-2">
        {leaders.slice(0, 3).map((player, idx) => (
          <div 
            key={`${player.name}-${idx}`}
            className="flex items-center gap-3 p-2 bg-zinc-800/50 border border-white/5"
          >
            <span 
              className="w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full"
              style={{ backgroundColor: color + '30', color }}
            >
              {idx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{player.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{player.team}</div>
            </div>
            <div className="text-lg font-bold" style={{ color }}>
              {player[statKey]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventPage;
